import i18n from 'scoreboard/components/i18n'
import Dataset from 'scoreboard/datasets/model/dataset'
import _ from 'lodash'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import formatValue from 'scoreboard/components/format-value'
import { getBreakdownPeriodLabel }
	from 'scoreboard/components/designer/breakdown-period-options'
import daysOfTheWeek from 'scoreboard/components/days-of-the-week'
import monthsOfTheYear from 'scoreboard/components/months-of-the-year'
import calendars from 'scoreboard/components/calendars'
import momentHelper from 'scoreboard/components/moment-helper'
import { ROLLUPS_NOT_VIEWABLE } from 'scoreboard/datasets/model/dataset'
import unwrapModel from 'scoreboard/components/unwrap-model'
import clusterLabel from 'scoreboard/datasets/edit/fields/cluster-label'
import { isMonthOfTheYear, isDayOfTheWeek } from 'scoreboard/datasets/is-compare-all-time'
import KeyDisplay from 'scoreboard/datasets/key-display'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import SummaryType from 'scoreboard/datasets/model/summary-type'
const { HOUR_OF_THE_DAY } = SummaryType
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT, YES_NO, DATE, DATE_TIME, TIME, CALENDAR_PERIOD, CLUSTER } = DataType

/**
 * @typedef {KeyDisplayOptions} FormattingKeyDisplayOptions
 * @property {Boolean} showKeys used to override isDisplayed behavior; the value
 * always returned by the overriding function
 */
class FormattingKeyDisplay extends KeyDisplay {
	constructor(options) {
		super(options)
		this.showKeys = options.showKeys
	}
	/** @override */
	isDisplayed() {
		return this.showKeys
	}
}

/**
 * @typedef {Object} FormatSummaryValueContext
 * @property {Dataset} [dataset]
 * @property {DatasetField} [datasetField]
 * @property {Object} [treeItemsById]
 * @property {Boolean} [valueIsRangeId=false] Treat the value as a range ID
 * @property {DatasetTabularGrouping} grouping
 * /

/**
 * Constructs a label for a field's value, e.g. converts a number breakdown
 * range into a String like "13 to 25". Note: the logic here closely matches
 * the logic in DatasetsQueryDao#transformForSummaryType
 * @param {Object} columnAttributes the field
 * @param {Object} value the value to be formatted
 * @param {FormatSummaryValueContext} context
 */
function formatSummaryValue(columnAttributes, value, context = {}) {
	if (value == Dataset.BLANK || _.isNil(value)) {
		return i18n.get('common.blank')
	}
	if (value == 'not-in-any') {
		if (columnAttributes.dataType == TEXT) {
			return i18n.get('datasets.filters.pretty.notInAnyCategory')
		}
		if (isNumericDataType(columnAttributes.dataType)) {
			return i18n.get('datasets.filters.pretty.notInAnyRange')
		}
		if (columnAttributes.dataType == DATE ||
			columnAttributes.dataType == DATE_TIME) {
			return i18n.get('datasets.filters.pretty.notInAnyPeriod')
		}
	}
	if (value == 'other') {
		return i18n.get('common.other')
	}

	if (columnAttributes.dataType === YES_NO) {
		return i18n.get('common.' + (value ? 'yes' : 'no'))
	}

	let timeFormat = momentHelper.getTimeFormat(columnAttributes.timeDisplayType)

	if (columnAttributes.summaryType === HOUR_OF_THE_DAY) {
		return formatValue({
			value: `${value}:00:00`,
			dataType: TIME,
			timeFormat
		})
	}

	if (columnAttributes.dataType === DATE ||
		columnAttributes.dataType === DATE_TIME) {
		if (isMonthOfTheYear(columnAttributes)) {
			return monthsOfTheYear()[_.lowerCase(value)]
		}
		if (isDayOfTheWeek(columnAttributes)) {
			return daysOfTheWeek()[_.lowerCase(value)]
		}
		if (columnAttributes.summaryType === 'similar-time-ranges') {
			let groupCalendarId =
				_.get(context, 'grouping.groupCalendarId') ||
				columnAttributes.groupCalendarId
			let breakdownCalendarId =
				_.get(context, 'grouping.breakdownCalendarId') ||
				columnAttributes.breakdownCalendarId
			const groupCalendarType =
				calendars.calendarsById[groupCalendarId].type
			const breakdownCalendarType =
				calendars.calendarsById[breakdownCalendarId].type
			return getBreakdownPeriodLabel({
				groupCalendarType,
				breakdownCalendarType,
				index: value
			})
		}
		let period = calendars.periodsById[value]
		if (_.isNil(period) && typeof value === 'string') {
			return _.capitalize(value)
		}
		return period.label
	}

	if (columnAttributes.dataType === TIME) {
		return formatValue({
			value,
			dataType: columnAttributes.dataType,
			timeFormat
		})
	}

	const getDatasetField = () => {
		return context.datasetField || context.dataset.combinedFieldsById[
			columnAttributes.datasetFieldId
		]
	}

	const field = getDatasetField()
	if (isGeographicOrRollup(columnAttributes.dataType) && context.treeItemsById) {
		let treeItem = context.treeItemsById[value]
		if (treeItem) {
			const keyDisplay = new FormattingKeyDisplay({
				model: context.dataset,
				field,
				treeItemsById: context.treeItemsById,
				showKeys: columnAttributes.showRollupKeys
			})
			return keyDisplay.labelAndKeys(value)
		} else if (_.isString(value)) {
			return value
		} else {
			return i18n.get('datasets.trees.item.deleted')
		}
	}

	if (columnAttributes.dataType === CALENDAR_PERIOD) {
		return _.get(calendars.periodsById[value], 'label', '')
	}

	const isRangeSummaryType = columnAttributes.summaryType === 'range'
	const lookupRangeLabel = context.valueIsRangeId || isRangeSummaryType
	if (lookupRangeLabel && _.get(field , 'ranges')) {
		var theRange = unwrapModel(field.ranges)
			.find(it => it.id == value)
		return _.isNil(theRange) ? i18n.get('common.other') :
			unwrapModel(theRange).label
	}

	if (isNumericDataType(columnAttributes.dataType)) {
		if (value === ROLLUPS_NOT_VIEWABLE) {
			return value
		}

		const shorten = !_.isNil(columnAttributes.abbreviateNumbers) ?
			columnAttributes.abbreviateNumbers : columnAttributes.abbreviated
		return formatValue({
			value,
			dataType: columnAttributes.dataType,
			currencyCode: columnAttributes.currencyCode,
			shorten,
			precision: columnAttributes.precision
		})
	}

	if (columnAttributes.dataType === CLUSTER) {
		return clusterLabel(value, columnAttributes.clusterLabels)
	}

	return value
}

export default formatSummaryValue
formatSummaryValue.__filename__ = 'datasets/overview/format-summary-value.js'
